import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, type U3144InitLU } from "./U3144Service";
import { U3144ContextProvider } from "./U3144Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3144";

export const U3144ROLES = {
  READ: "U3144:READ",
  ADD: "U3144:ADD",
  EDIT: "U3144:EDIT",
  DELETE: "U3144:DELETE",
  AUDITS: "U3144:AUDITS",
} as const;
export type U3144ROLES = (typeof U3144ROLES)[keyof typeof U3144ROLES];

const defaultInitLU: U3144InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  logicalUnitCode: "U3144",
};

const U3144: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3144InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u3144"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3144InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3144InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? "U3144",
      });
      setInitLUReady(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
    onError: ({ error }: { error: any }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné U3106

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t("title", { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3144ContextProvider initLU={initLUData}>
      <Outlet />
    </U3144ContextProvider>
  );
};

export default U3144;
